import React, { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Calendar,
  CheckCircle,
  Heart,
} from "lucide-react";
import emailjs from "@emailjs/browser";
import { facilitiesData } from "../data/facilities";

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    facility: "",
    subject: "",
    message: "",
    preferredContact: "phone",
    bestTime: "morning",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const contactReasons = [
    "Schedule a Tour",
    "General Information",
    "Pricing and Availability",
    "Memory Care Services",
    "Admission Process",
    "Insurance and Payment",
    "Other",
  ];

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); // Start loading

    // Replace these with your actual EmailJS credentials
    const serviceID = "service_u0hqedn";
    const templateID = "template_9l0w9sg";
    const publicKey = "3R6AKIQ_Qha6B19lQ";

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      facility: formData.facility,
      subject: formData.subject,
      message: formData.message,
      preferredContact: formData.preferredContact,
      bestTime: formData.bestTime,
      to_email: "info@almondtreeseniorcare.com", // The email you want to send to
    };

    try {
      await emailjs.send(serviceID, templateID, templateParams, publicKey);

      // If successful, show success state
      setIsSubmitted(true);
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        facility: "",
        subject: "",
        message: "",
        preferredContact: "phone",
        bestTime: "morning",
      });
      // Optionally reset success state after a delay
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("Failed to send message. Error:", error);
      alert(
        "Sorry, there was a problem sending your message. Please try again or contact us directly at info@aajyaan.com."
      );
    } finally {
      setIsLoading(false); // Stop loading in both success and error cases
    }
  };

  const facilityLocations = [
    {
      name: "Littleton Home",
      address: "5310 W Geddes Ave, Littleton, CO 80128",
      phone: "(720) 999-7773",
      email: "info@almondtreeseniorcare.com",
      specialty: "Assisted Living",
    },
    {
      name: "Highlands Ranch Home",
      address: "9896 Keenan St, Highlands Ranch, CO 80130",
      phone: "(720) 999-7773",
      email: "info@almondtreeseniorcare.com",
      specialty: "Assisted Living",
    },
    {
      name: "Centennial Home",
      address: "7364 S Fillmore Cir, Centennial, CO 80122",
      phone: "(720) 999-7773",
      email: "info@almondtreeseniorcare.com",
      specialty: "Memory Care",
    },
    {
      name: "Centennial Home II",
      address: "7021 S Ash Cir, Centennial, CO 80122",
      phone: "(720) 999-7773",
      email: "info@almondtreeseniorcare.com",
      specialty: "Memory Care",
    },
  ];

  return (
    <div className="pt-8" id="contact">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-green-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We're here to answer your questions and help you find the perfect
              care solution for your loved one. Contact us today to learn more
              or schedule a tour.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white" id="contactform">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    Send Us a Message
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="(000) 111-2222"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Facility of Interest
                      </label>
                      <select
                        name="facility"
                        value={formData.facility}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      >
                        <option value="">Select a facility</option>
                        {facilitiesData.map((facility: any) => (
                          <option key={facility.id} value={facility.id}>
                            {facility.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Please select a topic</option>
                      {contactReasons.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                      placeholder="Please tell us about your needs and any questions you have..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Contact Method
                      </label>
                      <select
                        name="preferredContact"
                        value={formData.preferredContact}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      >
                        <option value="phone">Phone</option>
                        <option value="email">Email</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Best Time to Contact
                      </label>
                      <select
                        name="bestTime"
                        value={formData.bestTime}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      >
                        <option value="morning">Morning (9 AM - 12 PM)</option>
                        <option value="afternoon">
                          Afternoon (12 PM - 5 PM)
                        </option>
                        <option value="evening">Evening (5 PM - 7 PM)</option>
                        <option value="anytime">Anytime</option>
                      </select>
                    </div>
                  </div>

                  {/* <button
                    type="submit"
                    className="w-full bg-primary-green text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    Send Message
                  </button> */}
                  <button
                    type="submit"
                    disabled={isLoading} // Disable button when loading
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Sending..." : "Send Message"}{" "}
                    {/* Change text based on state */}
                    {!isLoading && (
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-gray-600">
                    Thank you for your interest! We'll get back to you within 24
                    hours.
                  </p>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Contact Information
              </h2>

              <div className="bg-blue-50 rounded-xl p-8 mb-8">
                <div className="flex items-center mb-4">
                  <Heart className="h-8 w-8 text-primary-green mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Main Office
                  </h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-primary-green mr-3" />
                    <span className="text-gray-700">(720) 999-7773</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-primary-green mr-3" />
                    <span className="text-gray-700">
                      info@almondtreeseniorcare.com
                    </span>
                  </div>
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-primary-green mr-3 mt-1" />
                    <div className="text-gray-700">
                      <p className="font-medium mb-1">Business Hours:</p>
                      <p>Monday - Sunday: 8:00 AM - 6:00 PM</p>
                      <p className="mt-2 text-sm text-primary-green font-medium">
                        24/7 Emergency Support Available
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 text-green-600 mr-2" />
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  {/* <button className="w-full text-left p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors">
                    <span className="font-medium text-gray-900">
                      Schedule a Tour
                    </span>
                    <p className="text-sm text-gray-600">
                      Visit any of our four facilities
                    </p>
                  </button> */}
                  <button className="w-full text-left p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors">
                    <span className="font-medium text-gray-900">
                      Emergency Contact
                    </span>
                    <a href="tel:+17209997773">
                      <p className="text-sm text-gray-600">
                        24/7 support: (720) 999-7773
                      </p>
                    </a>
                  </button>
                  <a href="brochure.pdf" target="_blank">
                    <button className="w-full text-left p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors">
                      <span className="font-medium text-gray-900">
                        Download Our Brochure
                      </span>
                      <p className="text-sm text-gray-600">
                        Get detailed facility information
                      </p>
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Locations */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Facility Locations
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Each facility has dedicated staff ready to assist you. Contact the
              location that interests you most for personalized information.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {facilityLocations.map((facility, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {facility.name}
                    </h3>
                    <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {facility.specialty}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-gray-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{facility.address}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{facility.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">
                      {facility.email}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">24/7 Emergency Support</h2>
          <p className="text-xl mb-6 opacity-90">
            For urgent matters or emergencies involving current residents
          </p>
          <a
            href="tel:+17209997773"
            className="inline-flex items-center px-8 py-4 bg-white text-red-600 font-bold rounded-lg hover:bg-gray-100 transition-colors duration-300"
          >
            <Phone className="h-6 w-6 mr-3" />
            Call Emergency Line: (720) 999-7773
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
